import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Logo from "../AllImages/GetItRight.png";
import MajesticConcert from "../AllImages/Concert.jpg";

// Firebase imports
import { db } from "../firebase"; 
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";

// Create a default payments object for May–November
const initialMonthsState = {
  May: false,
  June: false,
  July: false,
  August: false,
  September: false,
  October: false,
  November: false,
};

// Helper function to create a new participant object
const getNewParticipant = () => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  age: "",
  role: "",
  instrument: "",
  nationality: "",
  address: "",
  dietary: "",     // optional
  singleBed: false,
  payments: { ...initialMonthsState },
  initialPayment: false, 
});

// Arrays for dropdown options
const countryOptions = [
  "United States",
  "United Kingdom",
  "Mexico",
  "Colombia",
  "Venezuela",
  "Guatemala",
  "El Salvador",
  "Honduras",
  "Nicaragua",
  "Costa Rica",
  "Panama",
  "Dominican Republic",
  "Puerto Rico",
  "Cuba",
  "Argentina",
  "Peru",
  "Chile",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Other",
];

const instrumentOptions = [
  "Guitar",
  "Piano",
  "Drums",
  "Violin",
  "Viola",
  "Cello",
  "Bass",
  "Trumpet",
  "Trombone",
  "Tuba",
  "French Horn",
  "Saxophone",
  "Flute",
  "Clarinet",
  "Oboe",
  "Basson",
  "Voice",
];

const OnboardPage = () => {
  const navigate = useNavigate();

  // Reservation details
  const [reservationType, setReservationType] = useState("single");
  const [groupCount, setGroupCount] = useState(1);

  // Each participant includes a "payments" object for May–November
  const [participants, setParticipants] = useState([getNewParticipant()]);

  // Primary contact
  const [primaryContact, setPrimaryContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });

  // Update participants array when reservation type or group count changes
  useEffect(() => {
    if (reservationType === "group") {
      const count = parseInt(groupCount, 10);
      if (count > participants.length) {
        setParticipants((prev) => [
          ...prev,
          ...Array.from({ length: count - prev.length }, () => getNewParticipant()),
        ]);
      } else if (count < participants.length) {
        setParticipants((prev) => prev.slice(0, count));
      }
    } else {
      setParticipants([getNewParticipant()]);
    }
  }, [groupCount, reservationType]);

  // Handle changes for participant fields
  const handleParticipantChange = (index, field, value) => {
    const newParticipants = [...participants];
    newParticipants[index][field] = value;
    setParticipants(newParticipants);
  };

  /**
   * Validate all required fields:
   *  - Primary contact: firstName, lastName, email, phone, password
   *  - Participants: firstName, lastName, email, phone, age, role, nationality, address
   *    * if role = musician => instrument required
   *  - dietary & singleBed are optional
   */
  const validateForm = () => {
    // Validate primary contact
    const { firstName, lastName, email, phone, password } = primaryContact;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !password.trim()) {
      alert("Please fill all required fields in Primary Contact (first name, last name, email, phone, password).");
      return false;
    }
    if (password.length < 6) {
      alert("Your password must be at least 6 characters long. Please try again.");
      return false;
    }

    // Validate participants
    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];
      if (
        !p.firstName.trim() ||
        !p.lastName.trim() ||
        !p.email.trim() ||
        !p.phone.trim() ||
        !p.age.toString().trim() ||
        !p.role.trim() ||
        !p.nationality.trim() ||
        !p.address.trim()
      ) {
        alert(`Please fill all required fields for Participant ${i + 1}.
- Required: first name, last name, email, phone, age, role, nationality, address.`);
        return false;
      }
      // if the participant is a musician, instrument is required
      if (p.role === "musician" && !p.instrument.trim()) {
        alert(`Please select an instrument for Participant ${i + 1} (role = musician).`);
        return false;
      }
    }

    return true; 
  };

  // Main form submission (Onboarding Only – no payment processing)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1) Basic validation of all required fields
    if (!validateForm()) {
      return; // stop submission if validation fails
    }
    
    // 2) Create the user in Firebase Authentication
    const auth = getAuth();
    let user;
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        primaryContact.email,
        primaryContact.password
      );
      user = userCredential.user;
      console.log("User created:", user.uid);
    } catch (err) {
      console.error("Error creating user in Firebase Auth:", err);
      alert("Error creating user. Please try again.");
      return;
    }

    // 3) Build the reservation data object
    const reservationData = {
      reservationType,
      groupCount: reservationType === "group" ? groupCount : 1,
      primaryContact: {
        firstName: primaryContact.firstName,
        lastName: primaryContact.lastName,
        email: primaryContact.email,
        phone: primaryContact.phone,
      },
      participants,
      cardInfo: null,
      studentAccepted: false,
      auditionPaid: false, 
      accountType: "Client",
      createdAt: serverTimestamp(),
      downPayment: false,
    };

    // 4) Save the reservation data to Firestore
    try {
      await setDoc(doc(db, "reservations", user.uid), reservationData);
      console.log("Reservation document created with ID:", user.uid);
    } catch (firestoreErr) {
      console.error("Error writing reservation to Firestore:", firestoreErr);
      alert("Error saving reservation. Please try again.");
      return;
    }

    // 5) Navigate the user to a dashboard or confirmation page
    navigate("/DashBoard", {
      state: {
        uid: user.uid,
        name: `${primaryContact.firstName} ${primaryContact.lastName}`,
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-800 text-gray-100">
      {/* Hero / Header Section */}
      <div className="relative h-48 md:h-64 lg:h-72 flex items-center justify-center">
        <img
          src={MajesticConcert}
          alt="Background"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center">
          <div className="flex flex-col items-center">
            <img src={Logo} alt="Main Logo" className="h-12 mb-3" />
            <h1 className="text-3xl md:text-4xl font-bold text-yellow-400">
            Registration Reservation
            </h1>
            <p className="text-sm md:text-base text-gray-200 mt-2">
            Secure your spot for an unforgettable orchestral experience!
            </p>
          </div>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="flex-grow py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Reservation Type */}
            <div className="bg-gray-700 p-4 rounded shadow">
              <label className="block text-lg font-medium text-yellow-400 mb-2">
              Reservation Type
              </label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center text-gray-200">
                  <input
                    type="radio"
                    name="reservationType"
                    value="single"
                    checked={reservationType === "single"}
                    onChange={() => setReservationType("single")}
                    className="mr-2 accent-yellow-500"
                  />
                     Individual
                </label>
                <label className="inline-flex items-center text-gray-200">
                  <input
                    type="radio"
                    name="reservationType"
                    value="group"
                    checked={reservationType === "group"}
                    onChange={() => setReservationType("group")}
                    className="mr-2 accent-yellow-500"
                  />
                   Group
                </label>
                {reservationType === "group" && (
                  <div className="flex items-center">
                    <label className="mr-2 text-gray-200">
                    Primary Contact
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={groupCount}
                      onChange={(e) => setGroupCount(e.target.value)}
                      className="border p-1 rounded w-20 bg-gray-600 text-gray-100"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Primary Contact */}
            <div className="bg-gray-700 p-4 rounded shadow">
              <h2 className="text-2xl font-semibold mb-2 text-yellow-400">
              Primary Contact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block font-medium text-gray-200">Name *</label>
                  <input
                    type="text"
                    value={primaryContact.firstName}
                    onChange={(e) =>
                      setPrimaryContact({ ...primaryContact, firstName: e.target.value })
                    }
                    className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-200">Last Name *</label>
                  <input
                    type="text"
                    value={primaryContact.lastName}
                    onChange={(e) =>
                      setPrimaryContact({ ...primaryContact, lastName: e.target.value })
                    }
                    className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                    placeholder="Doe"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-200">Email *</label>
                  <input
                    type="email"
                    value={primaryContact.email}
                    onChange={(e) =>
                      setPrimaryContact({ ...primaryContact, email: e.target.value })
                    }
                    className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-200">Cellphone *</label>
                  <input
                    type="text"
                    value={primaryContact.phone}
                    onChange={(e) =>
                      setPrimaryContact({ ...primaryContact, phone: e.target.value })
                    }
                    className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                    placeholder="+1 212 555 1234"
                  />
                </div>
                <div>
                  <label className="block font-medium text-gray-200">Password*</label>
                  <input
                    type="password"
                    value={primaryContact.password}
                    onChange={(e) =>
                      setPrimaryContact({ ...primaryContact, password: e.target.value })
                    }
                    className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                    placeholder="Enter Password"
                  />
                </div>
              </div>
            </div>

            {/* Participants */}
            <div className="bg-gray-700 p-4 rounded shadow">
              <h2 className="text-2xl font-semibold mb-2 text-yellow-400">
            Participants
              </h2>
              {participants.map((participant, index) => (
                <div
                  key={index}
                  className="border border-gray-600 p-4 rounded mb-4 bg-gray-800"
                >
                  <h3 className="font-semibold mb-2 text-gray-200">
                  Participant{index + 1}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium text-gray-200">Name *</label>
                      <input
                        type="text"
                        value={participant.firstName}
                        onChange={(e) =>
                          handleParticipantChange(index, "firstName", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="First Name"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">Last Name *</label>
                      <input
                        type="text"
                        value={participant.lastName}
                        onChange={(e) =>
                          handleParticipantChange(index, "lastName", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="Last Name"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">Email *</label>
                      <input
                        type="email"
                        value={participant.email}
                        onChange={(e) =>
                          handleParticipantChange(index, "email", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">CellPhone *</label>
                      <input
                        type="text"
                        value={participant.phone}
                        onChange={(e) =>
                          handleParticipantChange(index, "phone", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="+1 212 555 1234"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">Age *</label>
                      <input
                        type="number"
                        value={participant.age}
                        onChange={(e) =>
                          handleParticipantChange(index, "age", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="Age"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">Role *</label>
                      <select
                        value={participant.role}
                        onChange={(e) =>
                          handleParticipantChange(index, "role", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                      >
                        <option value="">Select Role</option>
                        <option value="chaperone">Chaperone</option>
                        <option value="musician">Musician</option>
                      </select>
                    </div>
                    {participant.role === "musician" && (
                      <div>
                        <label className="block font-medium text-gray-200">Instrument *</label>
                        <select
                          value={participant.instrument}
                          onChange={(e) =>
                            handleParticipantChange(index, "instrument", e.target.value)
                          }
                          className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        >
                          <option value="">Select Instrument</option>
                          {instrumentOptions.map((inst) => (
                            <option key={inst} value={inst}>
                              {inst}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block font-medium text-gray-200">Nationality *</label>
                      <select
                        value={participant.nationality}
                        onChange={(e) =>
                          handleParticipantChange(index, "nationality", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                      >
                        <option value="">Select Nationality</option>
                        {countryOptions.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">Address *</label>
                      <input
                        type="text"
                        value={participant.address}
                        onChange={(e) =>
                          handleParticipantChange(index, "address", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="Address"
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-200">
                      Dietary Restrictions (Optional)
                      </label>
                      <input
                        type="text"
                        value={participant.dietary}
                        onChange={(e) =>
                          handleParticipantChange(index, "dietary", e.target.value)
                        }
                        className="border p-2 rounded w-full bg-gray-600 text-gray-100"
                        placeholder="e.g., Vegan, Gluten-Free"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={participant.singleBed}
                        onChange={(e) =>
                          handleParticipantChange(index, "singleBed", e.target.checked)
                        }
                        className="mr-2 accent-yellow-500"
                      />
                      <label className="block font-medium text-gray-200">
                      Single Bed Option (+$150) (Optional)
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Shared Accommodation Notice */}
            <div className="bg-gray-800 text-gray-300 text-sm p-4 rounded-md mt-6 leading-relaxed border border-gray-700">
              <p className="font-semibold mb-2 text-yellow-400">Shared Accommodation Notice</p>
              <p>
              To keep the tour accessible, rooms include two queen-size beds shared among four participants—two per bed.
 </p>
              <p className="mt-2">
              A <span className="italic">“Single Bed”</span> option is available. If your group fills the room and you're under 15, you may share a bed with another similarly aged participant.
 </p>
              <p className="mt-2">
              If you're 15 or older and your group fills the room, selecting the “Single Bed” option is mandatory.
    </p>
        <p className="mt-2">
        Additional charges may apply, and availability is limited. Thank you for helping us make this experience safe and comfortable for everyone.
 </p>
        <p className="mt-4 text-gray-400 text-xs">
          ¿Preguntas? Contáctanos en <a href="mailto:information@simanof.org" className="underline text-gray-300">Information@simanof.org</a> o llama al <a href="tel:+17737109953" className="underline text-gray-300">(773) 710-9953</a>.
        </p>
      </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                className="bg-yellow-500 text-black px-6 py-3 rounded hover:bg-yellow-400 transition-colors font-semibold"
              >
  Submit Reservation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardPage;
